/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { AlertColor, SxProps } from '@mui/material';
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import type { TFunction } from 'i18next';
import React from 'react';
import {
  AlertBanner,
  dateUtils,
  LastUpdateTime,
  SwitchButton,
  useConfirmationDialog,
} from '@opengeoweb/shared';
import { Add, breakpoints } from '@opengeoweb/theme';
import { AreaList } from './AreaList';
import MaritimeWarningAvatar from './MaritimeWarningsAvatar';
import type {
  MaritimeWarningItem,
  MaritimeWarningItemAttributes,
  MaritimeWarningsConfig,
  DistrictsType,
} from './types';
import { useConfirmDeleteDialog } from './useConfirmDeleteDialog';
import { isWarningsValid } from './utils';
import { WarningList } from './WarningList/WarningList';
import { useWarningsTranslation } from '../../utils/i18n';

const spinnerSx: SxProps = {
  width: '16px!important',
  height: '16px!important',
  position: 'absolute',
  top: 0,
  bottom: 0,
  margin: 'auto',
  marginLeft: 1,
};

export const getWarningsValidity = (
  t: TFunction,
  warnings: MaritimeWarningItem[],
): string => {
  const validUntil =
    warnings.length > 0 && warnings[0].validUntil
      ? dateUtils.dateToString(
          dateUtils.createDate(warnings[0].validUntil),
          dateUtils.DATE_FORMAT_NAME_OF_DAY_MONTH,
        )
      : '';

  return validUntil ? `${t('maritime-warning-validuntil')}${validUntil}` : '';
};

export interface MaritimeWarningsFormProps {
  areas: DistrictsType[];
  selectedAreaIds: string[];
  warnings: MaritimeWarningItem[];
  modified: boolean;
  onToggleSelectArea: (id: string) => void;
  onSelectMultipleAreas: (ids: string[]) => void;
  onAddSelectedAreas: (id: string) => void;
  onRemoveWarning: (id: string) => void;
  onRemoveArea: (id: string) => void;
  onAddWarning: () => void;
  onChangeWarning: (
    id: string,
    newValues: Partial<MaritimeWarningItemAttributes>,
  ) => void;
  onMoveWarning: (oldIndex: number, newIndex: number) => void;
  onSubmit: (shouldExtend?: boolean) => Promise<void>;
  onClear: () => void;
  onToggleReadOnly: (isReadOnly: boolean) => void;
  isReadOnly: boolean;
  shouldHideEditorToggle: boolean;
  shouldShowDistricts?: boolean;
  editor?: string;
  error?: { title: string; severity: AlertColor };
  maritimeConfig: MaritimeWarningsConfig;
  lastUpdatedTime?: string;
}

export const MaritimeWarningsForm: React.FC<MaritimeWarningsFormProps> = ({
  areas,
  selectedAreaIds,
  modified,
  warnings,
  onToggleSelectArea,
  onSelectMultipleAreas,
  onAddSelectedAreas,
  onRemoveWarning,
  onRemoveArea,
  onAddWarning,
  onChangeWarning,
  onSubmit,
  onClear,
  onMoveWarning,
  onToggleReadOnly,
  isReadOnly,
  shouldHideEditorToggle,
  shouldShowDistricts = false,
  editor,
  error,
  maritimeConfig,
  lastUpdatedTime = '',
}: MaritimeWarningsFormProps) => {
  const [isPublishing, setIsPublishing] = React.useState(false);
  const [isExtending, setIsExtending] = React.useState(false);

  const { t } = useWarningsTranslation();
  const showConfirmDialog = useConfirmationDialog();

  const disabledAreaIds = warnings.flatMap(
    (warning) => warning.assignedAreaIds,
  );

  const { onRemoveWarningWithConfirmation } = useConfirmDeleteDialog({
    areas,
    warnings,
    maritimeConfig,
    onRemoveWarning,
  });

  const onClearWithConfirmation = React.useCallback(async () => {
    await showConfirmDialog({
      title: t('maritime-clear-dialog-title'),
      description: t('maritime-clear-dialog-description'),
      confirmLabel: t('warning-button-clear'),
      cancelLabel: t('close-dialog-cancel'),
    });
    onClear();
  }, [onClear, t, showConfirmDialog]);

  const isValidForPublish = isWarningsValid(warnings, maritimeConfig);
  const isValidForExtend = isWarningsValid(warnings, maritimeConfig, false);

  const onSwitchMode = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    const { checked } = event.target;
    const isReadOnly = !checked;
    if (modified && isReadOnly) {
      await showConfirmDialog({
        title: t('warning-view-mode-title'),
        description: t('warning-view-mode-description'),
        confirmLabel: t('warning-view-mode-confirm'),
        cancelLabel: t('warning-dialog-cancel'),
      });
    }
    onToggleReadOnly(isReadOnly);
  };

  const validTimes = getWarningsValidity(t, warnings);

  const onPressPublish = async (): Promise<void> => {
    if (!isPublishing) {
      setIsPublishing(true);
      try {
        await onSubmit();
      } catch {
        // onSubmit handles errors itself
      } finally {
        setIsPublishing(false);
      }
    }
  };

  const onPressExtend = async (): Promise<void> => {
    if (!isExtending) {
      setIsExtending(true);
      try {
        await onSubmit(true);
      } catch {
        // onSubmit handles errors itself
      } finally {
        setIsExtending(false);
      }
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        height: '100%',
        containerName: 'maritime',
        containerType: 'inline-size',
        padding: 2,

        // in a dialog there is an additional empty footer that messes up styling. This hack fixes that.
        '[role="dialog"] &': {
          paddingBottom: 0,
        },

        [`@container maritime (min-width: ${breakpoints.tablet}px)`]: {
          padding: 3,
        },
      }}
    >
      {!!error && (
        <AlertBanner
          severity={error.severity}
          shouldClose
          title={error.title}
        />
      )}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          alignItems: 'center',
          width: '100%',
          [`@container maritime (min-width: ${breakpoints.tablet}px)`]: {
            justifyContent: 'space-between',
          },
        }}
      >
        <Typography
          sx={{
            color: 'geowebColors.captions.captionStatus.rgba',
            fontSize: '10px',
            lineHeight: '1.6',
            letterSpacing: '0.33px',
            minWidth: '300px',
          }}
        >
          {validTimes}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            alignItems: 'center',
            height: '38px',
          }}
        >
          {!shouldHideEditorToggle && (
            <>
              <SwitchButton
                activeLabel={t('warning-editor-user-editor')}
                checked={!isReadOnly}
                inActiveLabel={t('warning-editor-user-viewer')}
                onChange={onSwitchMode}
              />
              <Box sx={{ width: '16px', marginLeft: 1 }}>
                {!!editor && <MaritimeWarningAvatar editor={editor} />}
              </Box>
              {!!lastUpdatedTime && (
                <Box sx={{ float: 'left', paddingTop: 0.5 }}>
                  <LastUpdateTime
                    lastUpdateTime={lastUpdatedTime}
                    showRefreshButton={false}
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>

      <AreaList
        areas={areas}
        disabled={disabledAreaIds}
        isReadOnly={isReadOnly}
        onSelectMultipleAreas={onSelectMultipleAreas}
        onToggleSelectArea={onToggleSelectArea}
        selected={selectedAreaIds}
      />

      <WarningList
        areas={areas}
        isReadOnly={isReadOnly}
        maritimeConfig={maritimeConfig}
        onAddSelectedAreas={onAddSelectedAreas}
        onChangeWarning={onChangeWarning}
        onMoveWarning={onMoveWarning}
        onRemoveArea={onRemoveArea}
        onRemoveWarning={onRemoveWarningWithConfirmation}
        onSelectMultipleAreas={onSelectMultipleAreas}
        onToggleSelectArea={onToggleSelectArea}
        selectedAreaIds={selectedAreaIds}
        shouldShowDistricts={shouldShowDistricts}
        warnings={warnings}
      />

      {!isReadOnly && (
        <Button
          onClick={onAddWarning}
          size="small"
          sx={{ alignSelf: 'flex-start', paddingInline: 2 }}
          variant="tertiary"
        >
          <Add />
          {t('maritime-new-warning')}
        </Button>
      )}

      {!isReadOnly && (
        <Stack
          direction="row"
          spacing={2}
          sx={{
            flexWrap: 'wrap',
            [`@container maritime (min-width: ${breakpoints.tablet}px)`]: {
              position: 'absolute',
              bottom: 0,
              right: 0,
              paddingInline: 2,
              paddingBottom: 2,
              // in a dialog there is an additional empty footer that messes up styling. This hack fixes that.
              '[role="dialog"] &': {
                paddingBottom: 0,
              },
            },
          }}
          useFlexGap
        >
          <Button
            disabled={
              modified || !isValidForExtend || isExtending || isPublishing
            }
            onClick={onPressExtend}
            size="small"
            sx={{ width: '134px' }}
            variant="primary"
          >
            <span style={{ position: 'relative' }}>
              {isExtending
                ? t('maritime-button-extending')
                : t('maritime-button-extend')}
              {!!isExtending && (
                <CircularProgress
                  color="inherit"
                  data-testid="spinner"
                  sx={spinnerSx}
                />
              )}
            </span>
          </Button>
          <Button
            onClick={onClearWithConfirmation}
            size="small"
            sx={{
              width: '134px',
              [`@container maritime (min-width: ${breakpoints.tablet}px)`]: {
                marginLeft: '8px',
              },
            }}
            variant="tertiary"
          >
            {t('warning-button-clear')}
          </Button>

          <Button
            disabled={
              !modified || !isValidForPublish || isPublishing || isExtending
            }
            onClick={onPressPublish}
            size="small"
            sx={{
              width: '134px',
              [`@container maritime (min-width: ${breakpoints.tablet}px)`]: {
                marginLeft: '8px',
              },
            }}
            variant="primary"
          >
            <span style={{ position: 'relative' }}>
              {isPublishing
                ? t('warning-button-publishing')
                : t('warning-button-publish')}
              {!!isPublishing && (
                <CircularProgress
                  color="inherit"
                  data-testid="spinner"
                  sx={spinnerSx}
                />
              )}
            </span>
          </Button>
        </Stack>
      )}
    </Box>
  );
};
